/**
 * The website's picture library — spec FR-I13, ADR-0021.
 *
 * Four calls and two helpers. The upload is multipart and therefore not `api()`,
 * for the reason `lib/api/documents.ts: upload` gives: `api()` sets a JSON
 * content type on any body, and a multipart body needs the browser to write its
 * own boundary.
 *
 * **Nothing here decides what an image may be.** The service decodes the bytes
 * and is the only judge (ADR-0021 §3); `refusalText` only puts its reasons into
 * words a firm can act on.
 */

import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import { SERVICE_API } from '../config'
import { session } from '../session'

export interface MediaUse {
  /** `booking` — a booking flow's payment QR (C25, Wave 27C). */
  kind: 'page' | 'service' | 'post' | 'logo' | 'favicon' | 'booking'
  label: string
  /** `draft` or `live` for a page; `null` otherwise. */
  where: 'draft' | 'live' | null
}

export interface SiteMediaItem {
  id: string
  /** First-party — `/site-media/<id>/<name>`. What a block stores. */
  url: string
  filename: string
  content_type: 'image/jpeg' | 'image/png' | 'image/webp'
  size_bytes: number
  width: number
  height: number
  alt: string | null
  created_at: string | null
  uses: MediaUse[]
}

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024
export const ACCEPT = 'image/jpeg,image/png,image/webp'

async function upload(file: File): Promise<{ item: SiteMediaItem; existing: boolean }> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
  const form = new FormData()
  form.append('file', file)
  const res = await session.authed(`${SERVICE_API}/api/v1/site/media`, {
    method: 'POST',
    headers: { [ORG_HEADER]: orgId },
    body: form,
  })
  const body = (await res.json().catch(() => null)) as
    | {
        success: boolean
        data: SiteMediaItem
        meta?: { existing?: boolean }
        error?: { code: string; message: string; details?: Record<string, unknown> }
      }
    | null
  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'The image could not be uploaded.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return { item: body.data, existing: body.meta?.existing === true }
}

export const siteMedia = {
  list: (params: { q?: string; offset?: number; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.q?.trim()) query.set('q', params.q.trim())
    if (params.offset) query.set('offset', String(params.offset))
    if (params.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query}` : ''
    return api<SiteMediaItem[]>(`/api/v1/site/media${suffix}`)
  },
  upload,
  setAlt: (id: string, alt: string) =>
    api<SiteMediaItem>(`/api/v1/site/media/${id}`, { method: 'PATCH', body: { alt } }),
  remove: (id: string) =>
    api<{ id: string; deleted: boolean }>(`/api/v1/site/media/${id}`, { method: 'DELETE' }),
}

/**
 * Checked in the browser too, **only to save the wait**: a 20 MB photograph
 * would upload in full before the service refused it. The service still
 * decides; this never lets through what it would not.
 */
export function precheck(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB. Images can be at most 5 MB.`
  }
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) {
    return 'SVG files cannot be uploaded — they can carry script. Use a PNG instead.'
  }
  return null
}

/** One line for where an image is used — `"About us (draft), Website logo"`. */
export function usesText(uses: MediaUse[]): string {
  return uses.map((u) => (u.where ? `${u.label} (${u.where})` : u.label)).join(', ')
}

/**
 * **The store itself refused** — Wave 8H, finding 4.
 *
 * Not a refusal of *this file*: the bucket is missing, this app may not write to
 * it, or it did not answer. The service says which in its own sentence
 * (`details.fault` is the coarse cause), and all this bundle does with the code
 * is stop the screen from blaming the firm's photograph — which is exactly what
 * a filename in front of the message does, and what a firm on UAT believed for
 * two days while `prodesk-site-media` did not exist on the box.
 */
export function isStorageUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'STORAGE_UNAVAILABLE'
}

/** What to tell the firm when the service refused. */
export function refusalText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong. Try again.'
  if (error.code === 'USAGE_LIMIT_EXCEEDED') {
    return "Your plan's storage is full. Free some space or upgrade the plan to upload more."
  }
  if (error.details.reason === 'in_use') {
    const uses = Array.isArray(error.details.uses) ? (error.details.uses as MediaUse[]) : []
    return `This image is used on ${usesText(uses)}. Replace it there first, then delete it.`
  }
  // `STORAGE_UNAVAILABLE` falls through here deliberately: the service writes
  // that sentence, it depends on which of four faults the store reported, and a
  // second copy in this file would be a second answer that drifts from it.
  return error.message
}

/**
 * The line for one failed upload — **with the file's name, or without it.**
 *
 * `${file.name}: ${refusalText(err)}` is right for every refusal that is about
 * the file (too large, not an image, an SVG) and wrong for the one refusal that
 * is not about any file. "photo.jpg: Your website's picture store has not been
 * set up in this environment" reads as a fact about photo.jpg.
 */
export function uploadFailureText(filename: string, error: unknown): string {
  return isStorageUnavailable(error) ? refusalText(error) : `${filename}: ${refusalText(error)}`
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
